// oxlint-disable no-restricted-imports
import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

import {
  Alert as AlertBase,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/primitives/alert";
import { cn } from "@/shared/lib/utils";

export type AlertVariant = "secondary" | "success" | "warning" | "destructive";

const colorClasses: Record<AlertVariant, string> = {
  secondary:
    "bg-secondary border-secondary-foreground/20 text-secondary-foreground [&_[data-slot=alert-description]]:text-secondary-foreground/70",
  destructive:
    "bg-destructive/10 border-destructive/30 text-destructive dark:bg-destructive/20 dark:border-destructive/40 [&_[data-slot=alert-description]]:text-destructive/80",
  warning:
    "bg-warning-container border-warning/30 text-warning-container-foreground [&>svg]:text-warning [&_[data-slot=alert-description]]:text-warning-container-foreground/80",
  success:
    "bg-success-container border-success/30 text-success-container-foreground [&>svg]:text-success [&_[data-slot=alert-description]]:text-success-container-foreground/80",
};

const calloutVariants = cva("p-4", {
  variants: {
    inline: {
      true: "has-data-[slot=alert-actions]:pr-4",
      false: "",
    },
  },
  defaultVariants: { inline: false },
});

export interface ColocaAlertProps
  extends Omit<React.ComponentProps<typeof AlertBase>, "variant">, VariantProps<typeof calloutVariants> {
  /**
   * Leading icon that reinforces the alert's intent at a glance. Use a single-purpose icon that matches the
   * variant — e.g. `<Info>` for secondary, `<CheckCircle2>` for success, `<AlertTriangle>` for warning,
   * `<XCircle>` for destructive.
   */
  icon?: LucideIcon;
  /**
   * Visual and semantic style of the alert. Defaults to `"secondary"`.
   *
   * - `"secondary"` — neutral information or system message
   * - `"success"` — confirms a completed action or positive outcome
   * - `"warning"` — non-blocking issue that requires attention
   * - `"destructive"` — error or action with irreversible consequences
   */
  variant?: AlertVariant;
  /**
   * Short title summarising the alert. Keep it to one line; use `text` for the longer description. Omit when
   * the `text` alone is self-explanatory.
   */
  heading?: React.ReactNode;
  /** Body copy that elaborates on the heading. Supports inline links via `<CAlertLink>`. */
  text?: React.ReactNode;
  /**
   * Action buttons rendered below the body copy (`inline=false`) or pinned to the trailing edge
   * (`inline=true`). Typically one or two `<ColocaButton size="sm">` elements.
   */
  actions?: React.ReactNode;
  /**
   * Dismiss control rendered absolutely in the top-right corner. Pass a `<ColocaCloseButton>` here. Only use
   * when the alert is genuinely dismissible; prefer `actions` for explicit user choices.
   */
  controls?: React.ReactNode;
  /**
   * Compact single-line layout. When `true`, `actions` are pinned to the right side of the alert instead of
   * stacking below the body. Useful for banners and notification bars.
   */
  inline?: boolean;
}

/**
 * Contextual feedback banner for in-page messages.
 *
 * Use `ColocaAlert` to surface information that is directly relevant to the current context — validation
 * errors after a form submit, a success confirmation after an operation, or a warning before an irreversible
 * action. Do not use it for global system notifications; use the notifications module instead.
 *
 * Compose with `heading` + `text` for structured messages, or pass `children` for fully custom content. Add
 * `actions` for user choices and `controls` for a dismiss affordance. Use `inline` for single-line banners.
 */
export function ColocaAlert({
  icon: Icon,
  variant = "secondary",
  color,
  heading,
  text,
  actions,
  controls,
  inline,
  className,
  children,
  ...props
}: ColocaAlertProps) {
  const hasChildren = React.Children.count(children) > 0;

  const body = hasChildren ? (
    children
  ) : (
    <>
      {heading != null && <AlertTitle>{heading}</AlertTitle>}
      {text != null && <AlertDescription>{text}</AlertDescription>}
    </>
  );

  return (
    <AlertBase className={cn(calloutVariants({ inline }), colorClasses[variant], className)} {...props}>
      {Icon && <Icon />}
      {body}
      {actions != null && (
        <div
          data-slot="alert-actions"
          className={cn(
            "flex flex-wrap items-center gap-2",
            inline
              ? "absolute top-1/2 right-4 -translate-y-1/2"
              : "col-start-1 mt-3 group-has-[>svg]/alert:col-start-2",
          )}
        >
          {actions}
        </div>
      )}
      {controls != null && <AlertAction>{controls}</AlertAction>}
    </AlertBase>
  );
}

export interface ColocaAlertLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Set to `true` when the href points to a page outside the dashboard — adds `target="_blank"` and
   * `rel="noopener noreferrer"` automatically. Omit for internal navigation links.
   */
  external?: boolean;
}

/**
 * Styled anchor for use inside `ColocaAlert` body text.
 *
 * Use it to link to a related resource or action within the alert's `text` prop — e.g. "See the full report"
 * or "Learn more". For clickable actions that trigger app behaviour, use `actions` with a `<ColocaButton>`
 * instead.
 */
export function ColocaAlertLink({ external, className, children, ...props }: ColocaAlertLinkProps) {
  const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <a
      className={cn("font-medium underline underline-offset-4 hover:opacity-80", className)}
      {...externalProps}
      {...props}
    >
      {children}
    </a>
  );
}

export { AlertTitle, AlertDescription, AlertAction };
